import {
  checkboxSlotRecipe as baseCheckboxSlotRecipe,
  radioGroupSlotRecipe as baseRadioGroupSlotRecipe,
  sliderSlotRecipe as baseSliderSlotRecipe,
  switchSlotRecipe as baseSwitchSlotRecipe,
} from '@chakra-ui/react/theme';

/**
 * Наведение, фокус и выключенность у флажка, тумблера и радио - одним правилом на всех.
 *
 * Правило то же, что уже действует для текстового поля: у всего, что можно нажать, есть
 * заметное наведение и видимое кольцо фокуса, а выключенное выглядит выключенным целиком,
 * вместе с подписью.
 *
 * Почему стоковых стилей не хватило. У флажка в базовом рецепте объявлен
 * `focusVisibleRing: 'outside'`, у радио - `_focusVisible` с обводкой. На экране не работало
 * ни то, ни другое: замерено на живом элементе, при фокусе на скрытом `<input>` рецепт ждёт
 * на слоте `control` атрибут `data-focus-visible`, а туда приходит только `data-focus`.
 * Правило было объявлено, но не срабатывало ни разу, поэтому с клавиатуры не было видно,
 * где ты стоишь. Здесь кольцо вешается на `_focus`, который приходит по-настоящему.
 *
 * У тумблера в стоке не было ни наведения, ни фокуса вовсе - только `_invalid`.
 */

// Кольцо фокуса - то же, что у текстового поля: цвет из палитры, снаружи, с отступом.
// Отступ обязателен: у флажка и радио контрол размером с текст, и кольцо впритык сливается
// с самой рамкой контрола.
const focusRing = {
  _focus: {
    outline: '2px solid',
    outlineColor: 'colorPalette.focusRing',
    outlineOffset: '2px',
  },
};

// Наведение поднимает контрол на ту же одну ступень, что строку списка, пункт меню и тихую
// кнопку: `bg.muted` - белый на 6%. Один цвет на всё, что светлеет.
//
// Только пока контрол пустой. Проверено на настройках уведомлений: у включённого тумблера
// заливка НЕ перекрывает подсветку, а подменяется ею - фиолетовый уходил в белый полупрозрачный,
// и под курсором включённый тумблер выглядел выключенным. Сигнал «включено» важнее отклика на
// наведение, поэтому у выбранных контролов заливку не трогаем.
//
// Состояние читаем прямо из `data-state`, который контрол проставляет себе сам (в разметке
// видно checked/unchecked). Короткие псевдоусловия вроде `_off` здесь молчат, а молчащее
// правило мы в этом же файле уже получали - ровно так не срабатывал заявленный фокус.
const whenEmpty = '&[data-state="unchecked"]';

// Выключенное гасится целиком, вместе с подписью. В стоке приглушался только сам контрол,
// поэтому недоступный вариант в списке читался как доступный: нажать нельзя, а выглядит
// как обычный. Замерено - у подписи не менялись ни цвет, ни прозрачность.
const dimmedLabel = {
  _disabled: { opacity: '0.5', cursor: 'not-allowed' },
};

export const checkboxSlotRecipe = {
  ...baseCheckboxSlotRecipe,
  base: {
    ...baseCheckboxSlotRecipe.base,
    control: {
      ...baseCheckboxSlotRecipe.base?.control,
      transition: 'background-color 120ms ease, outline-color 120ms ease',
      _hover: { [whenEmpty]: { bg: 'bg.muted' } },
      ...focusRing,
    },
    label: { ...baseCheckboxSlotRecipe.base?.label, ...dimmedLabel },
  },
};

export const switchSlotRecipe = {
  ...baseSwitchSlotRecipe,
  base: {
    ...baseSwitchSlotRecipe.base,
    control: {
      ...baseSwitchSlotRecipe.base?.control,
      _hover: { [whenEmpty]: { bg: 'bg.muted' } },
      ...focusRing,
    },
    label: { ...baseSwitchSlotRecipe.base?.label, ...dimmedLabel },
  },
};

export const radioGroupSlotRecipe = {
  ...baseRadioGroupSlotRecipe,
  base: {
    ...baseRadioGroupSlotRecipe.base,
    itemControl: {
      ...baseRadioGroupSlotRecipe.base?.itemControl,
      transition: 'background-color 120ms ease, outline-color 120ms ease',
      _hover: { [whenEmpty]: { bg: 'bg.muted' } },
      ...focusRing,
    },
    // Подпись гаснет вместе с кружком: недоступный вариант обязан выглядеть недоступным.
    itemText: { ...baseRadioGroupSlotRecipe.base?.itemText, ...dimmedLabel },
    item: { ...baseRadioGroupSlotRecipe.base?.item, ...dimmedLabel },
  },
};

/**
 * Ползунок гасится целиком, как и всё выключенное.
 *
 * Замерено на витрине: у выключенного стоит `data-disabled`, но прозрачность оставалась 1, а
 * заливка и кружок держали ту же фиолетовую заливку, что у рабочего. Отличить его от обычного
 * было нельзя - ровно та же дыра, что была у флажка и списка выбора.
 */
export const sliderSlotRecipe = {
  ...baseSliderSlotRecipe,
  base: {
    ...baseSliderSlotRecipe.base,
    root: {
      ...baseSliderSlotRecipe.base?.root,
      _disabled: { opacity: '0.5', cursor: 'not-allowed' },
    },
  },
};
